/**
 * ADMIN view of the moderation action log and the monthly CSV reports (requirements 8, 9.5).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/api-client';
import { currentJstMonth, formatJstDateTime } from '../../lib/date-utils';
import type { ModerationAction, ModerationActionType, MonthlyReportKind } from '../../lib/types';
import Pagination from '../Pagination';
import { ACTION_TYPE_LABELS, REASON_CATEGORY_LABELS } from './moderationLabels';

const PAGE_SIZE = 50;

export default function ModerationActionsPanel() {
  const [month, setMonth] = useState(currentJstMonth());
  const [actionType, setActionType] = useState<ModerationActionType | ''>('');
  const [actions, setActions] = useState<ModerationAction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<MonthlyReportKind | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await apiClient.listModerationActions({
        month: month || undefined, actionType: actionType || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
      });
      setActions(response.items);
      setTotal(response.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '措置記録を取得できませんでした');
    }
  }, [month, actionType, page]);

  useEffect(() => { void load(); }, [load]);

  const download = async (kind: MonthlyReportKind) => {
    if (!month) return;
    setDownloading(kind);
    try {
      await apiClient.downloadMonthlyReport(kind, month);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ダウンロードに失敗しました');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold">措置記録と月次レポート</h3>
      <p className="text-sm text-gray-600">
        措置記録は変更・削除できません。カード会社への月次レポートは、対象月（日本時間）を選んでCSVをダウンロードしてください。
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          対象月
          <input type="month" value={month} onChange={(event) => { setMonth(event.target.value); setPage(1); }} className="mt-1 block border border-gray-300 px-2 py-1" />
        </label>
        <label className="text-sm">
          措置の種類
          <select
            value={actionType}
            onChange={(event) => { setActionType(event.target.value as ModerationActionType | ''); setPage(1); }}
            className="mt-1 block border border-gray-300 px-2 py-1"
          >
            <option value="">すべて</option>
            {(Object.keys(ACTION_TYPE_LABELS) as ModerationActionType[]).map((type) => (
              <option key={type} value={type}>{ACTION_TYPE_LABELS[type]}</option>
            ))}
          </select>
        </label>
        <button type="button" disabled={!month || downloading !== null} onClick={() => void download('actions')} className="bg-pink-600 px-3 py-1.5 text-sm text-white disabled:opacity-60">
          {downloading === 'actions' ? '作成中...' : '措置記録CSV'}
        </button>
        <button type="button" disabled={!month || downloading !== null} onClick={() => void download('complaints')} className="bg-pink-600 px-3 py-1.5 text-sm text-white disabled:opacity-60">
          {downloading === 'complaints' ? '作成中...' : '通報・苦情CSV'}
        </button>
      </div>
      {error && <p className="bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>}

      <table className="w-full text-sm">
        <thead className="bg-gray-100 text-left">
          <tr>
            <th className="p-2">日時（JST）</th><th className="p-2">措置</th><th className="p-2">実行者</th>
            <th className="p-2">対象</th><th className="p-2">理由</th><th className="p-2">結果</th>
          </tr>
        </thead>
        <tbody>
          {actions.length === 0 ? (
            <tr><td colSpan={6} className="p-4 text-center text-gray-500">該当する措置はありません</td></tr>
          ) : actions.map((action) => (
            <tr key={action.id} className="border-b align-top">
              <td className="p-2 whitespace-nowrap">{formatJstDateTime(action.created_at)}</td>
              <td className="p-2">{ACTION_TYPE_LABELS[action.action_type]}</td>
              <td className="p-2">{action.actor_username}</td>
              <td className="p-2">
                {action.target_streamer_username ?? ''}
                {action.content_excerpt && <div className="text-xs text-gray-500">{action.content_excerpt}</div>}
              </td>
              <td className="p-2">
                {action.reason_category && <span className="mr-1 text-xs text-gray-500">[{REASON_CATEGORY_LABELS[action.reason_category]}]</span>}
                {action.reason}
              </td>
              <td className="p-2">
                {action.result === 'succeeded' ? '完了' : <span className="text-red-700" title={action.result_detail ?? ''}>一部失敗</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        currentPage={page} totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        totalItems={total} itemsPerPage={PAGE_SIZE} onPageChange={setPage}
      />
    </div>
  );
}
